import React, { useState } from 'react'
import styled from 'styled-components'
import { Bell } from 'phosphor-react'
import { Button, Checkbox } from 'semantic-ui-react'
import { completeTask, snoozeTask } from 'utility/services/crm'
import { formatShortDate, relativeDays } from 'utility/crm/dates'
import { invalidateCrm, useFollowups, useCrmToast, ALL_CRM_SCOPES } from 'hooks/crm'
import { CrmDrawer } from './CrmOverlay'
import { AsyncState, Muted, Pill, Row } from './ui'

const BellButton = styled.button`
    position: relative;
    background: transparent;
    border: 1px solid rgba(128, 128, 128, 0.4);
    border-radius: 8px;
    padding: 7px 9px;
    cursor: pointer;
    color: inherit;
    display: flex;
    .badge {
        position: absolute;
        top: -6px;
        right: -6px;
        min-width: 18px;
        height: 18px;
        padding: 0 4px;
        border-radius: 9px;
        background: #dc2626;
        color: #fff;
        font-size: 0.7rem;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`

const Group = styled.section`
    margin-bottom: 18px;
    h4 {
        margin: 0 0 6px;
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        opacity: 0.75;
    }
`

const Item = styled.div`
    border: 1px solid rgba(128, 128, 128, 0.3);
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 8px;
    .title {
        font-weight: 600;
    }
    .who {
        background: none;
        border: 0;
        padding: 0;
        color: #2563eb;
        cursor: pointer;
        text-align: left;
    }
`

const BUCKETS = [
    ['Overdue', 'Overdue', 'red'],
    ['Today', 'Today', 'amber'],
    ['Week', 'This week', 'blue'],
    ['Later', 'Later', 'gray'],
]

const SNOOZE_OPTIONS = [
    [1, 'Tomorrow'],
    [3, '3 days'],
    [7, '1 week'],
]

/**
 * Bell with the due-count badge and the "My follow-ups" drawer.
 * @param {{ onOpenContact: (contactId: number) => void, onOpenAccount: (accountId: number) => void }} props
 */
export const FollowupsBell = ({ onOpenContact, onOpenAccount }) => {
    const [open, setOpen] = useState(false)
    const [mine, setMine] = useState(true)
    const [busyId, setBusyId] = useState(null)
    const { data, loading, error, refetch } = useFollowups(mine)
    const { successToast, showError } = useCrmToast()
    // The badge always counts the user's own follow-ups, whatever the drawer is showing.
    const badgeQuery = useFollowups(true)
    const dueCount = badgeQuery.data?.DueCount || 0

    const run = async (task, action, message) => {
        setBusyId(task.TaskId)
        try {
            const result = await action()
            const next = result?.NextRecurring
            successToast(next ? `${message}. Next one is due ${formatShortDate(next.DueDate)}.` : message)
            invalidateCrm(...ALL_CRM_SCOPES)
        } catch (err) {
            showError(err)
            invalidateCrm('followups', 'tasks')
        } finally {
            setBusyId(null)
        }
    }

    const total = data ? BUCKETS.reduce((sum, [key]) => sum + (data[key]?.length || 0), 0) : 0

    return (
        <>
            <BellButton
                type="button"
                aria-label={dueCount ? `Follow-ups, ${dueCount} due` : 'Follow-ups'}
                onClick={() => setOpen(true)}
            >
                <Bell size={20} />
                {dueCount > 0 ? <span className="badge">{dueCount > 99 ? '99+' : dueCount}</span> : null}
            </BellButton>

            <CrmDrawer open={open} onClose={() => setOpen(false)} title={mine ? 'My follow-ups' : 'Team follow-ups'}>
                <Row $between $wrap style={{ marginBottom: 14 }}>
                    <Checkbox
                        toggle
                        label="Only mine"
                        checked={mine}
                        onChange={(_, d) => setMine(Boolean(d.checked))}
                    />
                    <Muted>{data ? `${data.DueCount} due` : ''}</Muted>
                </Row>
                <AsyncState
                    loading={loading}
                    error={error}
                    hasData={Boolean(data)}
                    empty={Boolean(data) && total === 0}
                    emptyText="No follow-ups. You're all caught up."
                    onRetry={refetch}
                >
                    {data
                        ? BUCKETS.map(([key, label, tone]) => {
                              const tasks = data[key] || []
                              if (!tasks.length) return null
                              return (
                                  <Group key={key} aria-label={label}>
                                      <h4>
                                          {label} <Pill $tone={tone}>{tasks.length}</Pill>
                                      </h4>
                                      {tasks.map(task => (
                                          <Item key={task.TaskId}>
                                              <div className="title">{task.Title}</div>
                                              <Muted>
                                                  {task.DueDate
                                                      ? `${formatShortDate(task.DueDate)} · ${relativeDays(task.DueDate)}`
                                                      : 'No date'}
                                                  {task.Recur ? ` · repeats ${task.Recur}` : ''}
                                                  {task.OwnerName ? ` · ${task.OwnerName}` : ''}
                                              </Muted>
                                              {task.ContactName || task.AccountName ? (
                                                  <div>
                                                      <button
                                                          type="button"
                                                          className="who"
                                                          onClick={() => {
                                                              setOpen(false)
                                                              if (task.ContactId) onOpenContact(task.ContactId)
                                                              else if (task.AccountId) onOpenAccount(task.AccountId)
                                                          }}
                                                      >
                                                          {task.ContactName || task.AccountName}
                                                      </button>
                                                      {task.ContactName && task.AccountName ? (
                                                          <Muted> · {task.AccountName}</Muted>
                                                      ) : null}
                                                  </div>
                                              ) : null}
                                              <Row $gap={6} $wrap style={{ marginTop: 8 }}>
                                                  <Button
                                                      size="mini"
                                                      primary
                                                      loading={busyId === task.TaskId}
                                                      disabled={busyId === task.TaskId}
                                                      onClick={() =>
                                                          run(task, () => completeTask(task.TaskId), 'Follow-up completed')
                                                      }
                                                  >
                                                      Done
                                                  </Button>
                                                  {SNOOZE_OPTIONS.map(([days, text]) => (
                                                      <Button
                                                          key={days}
                                                          size="mini"
                                                          disabled={busyId === task.TaskId}
                                                          aria-label={`Snooze ${task.Title} until ${text}`}
                                                          onClick={() =>
                                                              run(
                                                                  task,
                                                                  () => snoozeTask(task.TaskId, days),
                                                                  `Snoozed ${text.toLowerCase()}`,
                                                              )
                                                          }
                                                      >
                                                          Snooze {text.toLowerCase()}
                                                      </Button>
                                                  ))}
                                              </Row>
                                          </Item>
                                      ))}
                                  </Group>
                              )
                          })
                        : null}
                </AsyncState>
            </CrmDrawer>
        </>
    )
}
